import { useState, useEffect } from 'react';
import { getMergePreview, mergeUsers } from '../../../services/adminService';

const PROFILE_FIELDS = [
  { key: 'email', label: 'Email', pick: (u) => u?.email || '—' },
  { key: 'name', label: 'Full name', pick: (u) => u?.name || '—' },
  { key: 'given_name', label: 'Given name', pick: (u) => u?.given_name || '—' },
  { key: 'family_name', label: 'Family name', pick: (u) => u?.family_name || '—' },
  { key: 'phoneNumber', label: 'Phone', pick: (u) => u?.phoneNumber || '—' },
  { key: 'userSource', label: 'Source', pick: (u) => u?.userSource || '—' },
  { key: 'notes', label: 'Admin notes', pick: (u) => (u?.notes && String(u.notes).trim()) || '—' },
  { key: 'role', label: 'Role', pick: (u) => u?.role || '—' },
];

function formatConflictDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

export default function MergeUsersModal({
  token,
  secondaryUserId,
  secondarySummary,
  onClose,
  onSuccess,
}) {
  const [primaryUserId, setPrimaryUserId] = useState('');
  const [preview, setPreview] = useState(null);
  const [profileChoices, setProfileChoices] = useState({});
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPrimaryUserId('');
    setPreview(null);
    setProfileChoices({});
    setError(null);
  }, [secondaryUserId]);

  const loadPreview = async () => {
    const trimmed = primaryUserId.trim();
    if (!trimmed) {
      setError('Enter the Auth0 user id for the account you want to keep.');
      return;
    }
    if (trimmed === secondaryUserId) {
      setError('Primary and secondary must be different accounts.');
      return;
    }
    setLoadingPreview(true);
    setError(null);
    try {
      const data = await getMergePreview(token, {
        primaryUserId: trimmed,
        secondaryUserId,
      });
      setPreview(data);
      const next = {};
      for (const { key } of PROFILE_FIELDS) {
        next[key] = data.suggestedProfile?.[key] || 'primary';
      }
      setProfileChoices(next);
    } catch (e) {
      setPreview(null);
      setError(e.message || 'Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  };

  const setChoice = (key, value) => {
    setProfileChoices((prev) => ({ ...prev, [key]: value }));
  };

  const runMerge = async () => {
    const trimmed = primaryUserId.trim();
    if (!preview || !trimmed) return;
    const ok = window.confirm(
      'This will move all passes, bookings, and renewals into the kept account and permanently delete the duplicate user document. The removed Auth0 login will still exist in Auth0 until you delete it there. Continue?'
    );
    if (!ok) return;
    setMerging(true);
    setError(null);
    try {
      await mergeUsers(token, {
        primaryUserId: trimmed,
        secondaryUserId,
        profile: profileChoices,
      });
      onSuccess?.(trimmed);
      onClose();
    } catch (e) {
      setError(e.message || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-lg p-4 sm:p-6 max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-xl">
        <div className="flex justify-between items-start gap-2 mb-4">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Merge duplicate accounts</h2>
            <p className="text-sm text-gray-600 mt-1">
              All data moves into the <strong>kept</strong> account (primary). The duplicate profile here will be
              deleted:{' '}
              <span className="font-mono text-xs break-all text-gray-800">{secondaryUserId}</span>
              {secondarySummary ? (
                <span className="block text-gray-500 mt-0.5">
                  {secondarySummary}
                </span>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full text-xl font-bold"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3 mb-4">
          <label className="block text-sm font-medium text-gray-700">Auth0 user id to keep (primary)</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={primaryUserId}
              onChange={(e) => setPrimaryUserId(e.target.value)}
              placeholder="e.g. auth0|… or google-oauth2|…"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
            />
            <button
              type="button"
              onClick={loadPreview}
              disabled={loadingPreview}
              className="px-4 py-2 rounded-lg bg-brown text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {loadingPreview ? 'Loading…' : 'Load preview'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-800 text-sm" role="alert">
            {error}
          </div>
        )}

        {preview && (
          <>
            <div className="mb-4 p-3 rounded-lg bg-amber-50 text-amber-900 text-sm border border-amber-100">
              <p className="font-semibold mb-1">Summary</p>
              <ul className="list-disc list-inside space-y-0.5 text-amber-900/90">
                <li>
                  Passes: {preview.counts.passesPrimary} (kept) + {preview.counts.passesSecondary} (duplicate) → all on
                  kept account
                </li>
                <li>
                  Subscription renewals: {preview.counts.subscriptionRenewalsPrimary} +{' '}
                  {preview.counts.subscriptionRenewalsSecondary}
                </li>
                <li>
                  Confirmed bookings (user records): {preview.counts.userConfirmedBookingsPrimary} +{' '}
                  {preview.counts.userConfirmedBookingsSecondary} → merged and de-duplicated by class
                </li>
              </ul>
              {preview.warnings?.length > 0 && (
                <ul className="mt-2 list-disc list-inside text-amber-900 font-medium">
                  {preview.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}
            </div>

            {preview.classBookingConflicts?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-800 mb-2">Classes with both accounts booked (confirmed)</p>
                <ul className="max-h-36 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 text-sm">
                  {preview.classBookingConflicts.map((c) => (
                    <li key={c.classId} className="px-3 py-2">
                      <span className="font-medium">{c.title || 'Class'}</span>
                      <span className="text-gray-500 ml-2">{formatConflictDate(c.start)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-sm text-gray-600 mb-2">
              Suggested defaults favor verified email/phone, longer notes, and the higher role. Choose which account wins
              for each field:
            </p>
            <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Field</th>
                    <th className="text-left py-2 px-2 font-semibold text-gray-700">Kept account</th>
                    <th className="text-left py-2 px-2 font-semibold text-gray-700">Duplicate</th>
                  </tr>
                </thead>
                <tbody>
                  {PROFILE_FIELDS.map(({ key, label, pick }) => (
                    <tr key={key} className="border-b border-gray-100 last:border-0 align-top">
                      <td className="py-2 px-3 font-medium text-gray-800">{label}</td>
                      <td className="py-2 px-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`merge-${key}`}
                            checked={profileChoices[key] === 'primary'}
                            onChange={() => setChoice(key, 'primary')}
                            className="mt-1"
                          />
                          <span className="text-gray-700 break-words">{pick(preview.primary)}</span>
                        </label>
                      </td>
                      <td className="py-2 px-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`merge-${key}`}
                            checked={profileChoices[key] === 'secondary'}
                            onChange={() => setChoice(key, 'secondary')}
                            className="mt-1"
                          />
                          <span className="text-gray-700 break-words">{pick(preview.secondary)}</span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runMerge}
                disabled={merging}
                className="px-4 py-2 rounded-lg bg-red-700 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {merging ? 'Merging…' : 'Merge and delete duplicate'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
